import { type V2, dist } from '../../core/math';
import type { Actor } from '../../sim/actor';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { scaleShape } from '../../sim/shapes';
import { T, forEachHit, tripod } from '../../sim/skills';
import type { AreaSpec, HitSpec, ResolvedSkill, SkillAction, SkillDef, StatusApply } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import { removeStatus } from '../../sim/statusSystem';
import type { Reaction } from '../../sim/types';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, cone, dashSkill, hit, identityOp, invuln, party, proj, rect, ring, self, skill, stage } from './kit';

/**
 * CANTOR — hymn-priest support (buff / brand / shield / heal archetype).
 * Hits and buffing allies fill three Resonance bars; Anthem of Valor (Z) and
 * Hymn of Mending (X) consume every full bar for a party damage buff or a
 * party heal-over-time. Party effects are `party` actions or friendly hits,
 * so each ally's own peer applies them (victim-side routing).
 */

const HUE = 0xffe28a;
/** Radius of every party-wide effect. */
const PARTY_R = 24;
const BAR = 100;
const MAX_BARS = 3;
/** Resonance per ally buffed (+5% of a bar). */
const ALLY_GAIN = 5;
/** Resonance from a successful Thunder Prelude counter (+20% of a bar). */
const COUNTER_GAIN = 20;

const FLINCH: Reaction = { kind: 'flinch' };

/**
 * Party-wide friendly pulses (shields, zone buffs) use a zero-inner ring: it
 * targets exactly the same disc as a circle but renders as a clean expanding
 * ring instead of a ground-cracking shockwave.
 */
const aura = (r: number) => ring(0, r);

registerStatus({ id: 'can_rally', name: 'Rally', kind: 'buff', icon: 'can_rally', description: 'Attack power increased. Rally from different Cantor skills does not stack.', stacking: 'strongest', valueMod: 'atkPct', color: 0xffd860 });
registerStatus({ id: 'can_refrain', name: 'Refrain', kind: 'buff', icon: 'can_refrain', description: 'Attack speed increased; MP regeneration +12%.', stacking: 'strongest', valueMod: 'atkSpeedPct', mods: { mpRegenPct: 0.12 }, color: 0xfff0a0 });
registerStatus({ id: 'can_anthem', name: 'Anthem of Valor', kind: 'buff', icon: 'can_anthem', description: 'Damage dealt increased.', stacking: 'strongest', valueMod: 'dmgPct', color: 0xffb640 });
registerStatus({ id: 'can_mending1', name: 'Hymn of Mending', kind: 'buff', icon: 'can_mending', description: 'Recovers HP every 1.75 s.', stacking: 'strongest', tick: { interval: 1.75, kind: 'heal' }, color: 0x9dff9a });
registerStatus({ id: 'can_mending2', name: 'Hymn of Mending', kind: 'buff', icon: 'can_mending', description: 'Recovers HP every 1.5 s.', stacking: 'strongest', tick: { interval: 1.5, kind: 'heal' }, color: 0x9dff9a });
registerStatus({ id: 'can_mending3', name: 'Hymn of Mending', kind: 'buff', icon: 'can_mending', description: 'Recovers HP every 1.5 s.', stacking: 'strongest', tick: { interval: 1.5, kind: 'heal' }, color: 0x9dff9a });
registerStatus({ id: 'can_sanctuary', name: 'Sanctuary', kind: 'buff', icon: 'can_sanctuary', description: 'Damage taken greatly reduced.', stacking: 'strongest', valueMod: 'dmgReduction', color: 0xfff6c8 });
registerStatus({ id: 'can_canon', name: 'Guardian Canon', kind: 'buff', icon: 'can_canon', description: 'Damage taken reduced.', stacking: 'strongest', valueMod: 'dmgReduction', color: 0xc8d8ff });
registerStatus({ id: 'can_solo', name: 'Solo Anthem', kind: 'buff', icon: 'can_solo', description: 'Critical rate +20%.', stacking: 'refresh', mods: { critRate: 0.2 }, color: 0xffd24a });
registerStatus({ id: 'can_lightward', name: 'Light Ward', kind: 'debuff', icon: 'can_lightward', description: 'Channelling Light Ward: damage dealt -50%.', stacking: 'refresh', mods: { dmgPct: -0.5 }, color: 0xfff6c8 });
registerStatus({ id: 'can_dirge', name: 'Dirge', kind: 'debuff', icon: 'can_dirge', description: 'Attack power -30%.', stacking: 'refresh', mods: { atkPct: -0.3 }, cleansable: true, color: 0x9a7fb0 });
registerStatus({ id: 'can_frost', name: 'Frost Chime', kind: 'debuff', icon: 'can_frost', description: 'Move speed -60%.', stacking: 'refresh', mods: { moveSpeedPct: -0.6 }, element: 'water', cleansable: true, color: 0x7fd6ff });
registerStatus({ id: 'can_awe', name: 'Overawed', kind: 'debuff', icon: 'can_awe', description: 'Attack -30%, attack speed -20%, move speed -40%.', stacking: 'refresh', mods: { atkPct: -0.3, atkSpeedPct: -0.2, moveSpeedPct: -0.4 }, cleansable: true, color: HUE });
registerStatus({ id: 'can_ring', name: 'Lingering Chime', kind: 'debuff', icon: 'can_ring', description: 'The chime keeps ringing: damage every 0.5 s.', stacking: 'refresh', tick: { interval: 0.5, kind: 'damage' }, cleansable: true, color: HUE });

// ------------------------------------------------------------------ skills

const chimeBolt = skill({
  id: 'can_chime', name: 'Chime Bolt', hue: HUE, icon: 'note',
  description: 'Loose a ringing bolt up to 14 m that bursts in a 2.5 m chime on impact.',
  castType: 'normal', cooldown: 8, mana: 60, identityGain: 12,
  stages: [stage('Shoot', 0.45, [proj(0.15, { speed: 24, range: 14, radius: 0.5, height: 1.3, vfx: 'orb', hit: { shape: circle(0.5), coef: 0 }, explode: { shape: circle(2.5), coef: 3.5, reaction: FLINCH } })])],
});

const rallyingRefrain = skill({
  id: 'can_refrain', name: 'Rallying Refrain', hue: HUE, icon: 'note',
  description: 'A rousing refrain strikes foes around you and grants the party within 24 m Rally (+12% attack power), +4% attack speed and +12% MP regeneration for 8 s.',
  castType: 'normal', cooldown: 30, mana: 120, positional: 'back', superArmor: 'paralysis', identityGain: 8,
  stages: [
    stage('Buff', 0.6, [
      hit(0.25, circle(4), 2, { reaction: FLINCH }),
      party(0.25, PARTY_R, { id: 'can_rally', duration: 8, value: 0.12 }, { id: 'can_refrain', duration: 8, value: 0.04 }),
      identityOp(0.25, 'allies', PARTY_R),
    ]),
  ],
});

const resonantMark = skill({
  id: 'can_mark', name: 'Resonant Mark', hue: HUE,
  description: 'Two resonant lines tear 10 m forward and brand every foe they cross: damage taken from your party +10% for 10 s.',
  castType: 'normal', cooldown: 21, mana: 100, superArmor: 'paralysis', stagger: 'low', weakPoint: 1, identityGain: 20,
  synergy: { id: 'brand', duration: 10 },
  stages: [
    stage('Cast', 0.7, [
      hit(0.3, rect(10, 1.5, 0, -8), 3.5, { reaction: FLINCH }),
      hit(0.3, rect(10, 1.5, 0, 8), 3.5, { reaction: FLINCH, weakPoint: 0 }),
    ]),
  ],
});

const wardingChorus = skill({
  id: 'can_chorus', name: 'Warding Chorus', hue: HUE, icon: 'shield',
  description: 'A gust of choral wind blasts foes in a 6 m cone while the party within 24 m gains a shield worth 30% of your max HP for 4 s.',
  castType: 'normal', cooldown: 18, mana: 95, positional: 'back', superArmor: 'paralysis', identityGain: 12,
  stages: [
    stage('Cast', 0.6, [
      hit(0.25, cone(6, 90), 2.5, { reaction: { kind: 'knockback', power: 2.5 } }),
      hit(0.25, aura(PARTY_R), 0, { friendly: true, shieldPct: 0.3, shieldDuration: 4 }),
      identityOp(0.25, 'allies', PARTY_R),
    ]),
  ],
});

const CRUSH_R = 3.5;
/** Sonic Crush coefficients: the lift and the slam (950% in total). */
const CRUSH_LIFT = 3.5;
const CRUSH_SLAM = 6;
const sonicCrush = skill({
  id: 'can_crush', name: 'Sonic Crush', hue: HUE,
  description: 'Drop a sonic orb within 12 m that lifts foes, then slams them down. The aftershock rings for 3 s: allies standing in it gain Rally (+12% attack power; does not stack with Rallying Refrain).',
  castType: 'point', cooldown: 24, mana: 110, maxRange: 12, superArmor: 'paralysis', stagger: 'midHigh', weakPoint: 2, identityGain: 20,
  stages: [
    stage('Cast', 0.9, [
      area(0.1, { shape: circle(CRUSH_R), anchor: 'point', delay: 0.3, telegraph: 'none', hit: { shape: circle(CRUSH_R), coef: CRUSH_LIFT, reaction: { kind: 'launch', power: 0, duration: 0.8 }, stagger: 'low', weakPoint: 0 } }),
      area(0.1, { shape: circle(CRUSH_R), anchor: 'point', delay: 0.75, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(CRUSH_R), coef: CRUSH_SLAM, reaction: { kind: 'knockdown', duration: 1, power: 0 } } }),
      area(0.1, { shape: aura(CRUSH_R), anchor: 'point', delay: 0.75, duration: 3, tickInterval: 0.5, telegraph: 'none', hit: { shape: aura(CRUSH_R), coef: 0, friendly: true, status: [{ id: 'can_rally', duration: 8, value: 0.12 }] } }),
      identityOp(0.85, 'alliesAt', CRUSH_R),
    ]),
  ],
});

const SANCT_R = 5;
/** One Sanctuary Hymn pulse: damage to foes, damage reduction + shield to allies inside. */
function sanctuaryPulse(at: number, first: boolean): SkillAction[] {
  return [
    hit(at, circle(SANCT_R), 2.5, { anchor: 'point', reaction: FLINCH, ...(first ? {} : { weakPoint: 0 }) }),
    hit(at, aura(SANCT_R), 0, { anchor: 'point', friendly: true, shieldPct: 0.1, shieldDuration: 3, status: [{ id: 'can_sanctuary', duration: 1.6, value: 0.6 }] }),
  ];
}

const sanctuaryHymn = skill({
  id: 'can_sanctuary', name: 'Sanctuary Hymn', hue: HUE,
  description: 'Channel a hymn on a spot within 10 m for 4.5 s. Three pulses strike foes within 5 m; allies inside take 60% less damage and gain a shield worth 10% of your max HP. Moving cancels the hymn.',
  castType: 'point', cooldown: 24, mana: 110, maxRange: 10, superArmor: 'paralysis', weakPoint: 1, identityGain: 18,
  // Cancellable almost at once: walking away (or any other action) ends the channel.
  stages: [stage('Channel', 4.5, [0.3, 1.8, 3.3].flatMap((t, i) => sanctuaryPulse(t, i === 0)), { cancelAt: 0.3 })],
});

const thunderPrelude = skill({
  id: 'can_prelude', name: 'Thunder Prelude', hue: HUE,
  description: 'Strike the ground with the bell-staff: a 5 m thunderclap that stuns foes for 1.5 s. Counters blue-glowing enemies; a successful counter fills 20% of a Resonance bar.',
  castType: 'normal', cooldown: 16, mana: 90, positional: 'back', counter: true, stagger: 'low', identityGain: 14,
  stages: [stage('Slam', 0.6, [hit(0.25, circle(5), 4.5, { status: [{ id: 'stun', duration: 1.5 }], vfx: 'heavy' })])],
});

const CANON_TIME = 8;
const guardianCanon = skill({
  id: 'can_canon', name: 'Guardian Canon', hue: HUE, icon: 'shield',
  description: 'A protective canon: the party within 24 m takes 20% less damage for 8 s.',
  castType: 'normal', cooldown: 30, mana: 120,
  stages: [stage('Buff', 0.5, [party(0.2, PARTY_R, { id: 'can_canon', duration: CANON_TIME, value: 0.2 }), identityOp(0.2, 'allies', PARTY_R)])],
});

const aegisCrescendo = skill({
  id: 'can_aegis', name: 'Aegis Crescendo', hue: 0xfff0b0,
  description: 'APEX ART. A soaring crescendo: foes within 6 m are struck and overawed (attack -30%, attack speed -20%, move speed -40% for 10 s) while the party within 24 m gains a shield worth 100% of your max HP for 10 s. Fills one Resonance bar.',
  castType: 'normal', cooldown: 300, mana: 0, kind: 'awakening', superArmor: 'full', stagger: 'mid',
  stages: [
    stage('Buff', 1.0, [
      invuln(0, 0.8),
      hit(0.4, circle(6), 12, { reaction: { kind: 'knockback', power: 2 }, status: [{ id: 'can_awe', duration: 10 }], vfx: 'heavy' }),
      hit(0.4, aura(PARTY_R), 0, { friendly: true, shieldPct: 1, shieldDuration: 10 }),
      identityOp(0.4, 'gain', BAR),
    ]),
  ],
});

// Identity skills: one variant per bar count, chosen on Z/X by the identity.
const ANTHEM: readonly [number, number][] = [[0.05, 8], [0.1, 12], [0.15, 16]];
const anthems = ANTHEM.map(([value, duration], i) =>
  skill({
    id: `can_anthem${i + 1}`, name: 'Anthem of Valor', hue: 0xffb640, kind: 'identity', icon: 'note',
    description: `Consumes ${i + 1} Resonance bar${i ? 's' : ''}: the party within 24 m deals +${Math.round(value * 100)}% damage for ${duration} s.`,
    castType: 'normal', cooldown: 1, mana: 0, superArmor: 'push',
    stages: [stage('Buff', 0.5, [party(0.2, PARTY_R, { id: 'can_anthem', duration, value })])],
  }),
);

/** [heal per tick (fraction of max HP), ticks, seconds between ticks]. */
const MENDING: readonly [number, number, number][] = [[0.04, 8, 1.75], [0.06, 12, 1.5], [0.08, 16, 1.5]];
const mendings = MENDING.map(([value, ticks, every], i) =>
  skill({
    id: `can_mending${i + 1}`, name: 'Hymn of Mending', hue: 0x9dff9a, kind: 'identity', icon: 'buff',
    description: `Consumes ${i + 1} Resonance bar${i ? 's' : ''}: the party within 24 m recovers ${Math.round(value * 100)}% max HP ${ticks} times over ${Math.round(ticks * every)} s.`,
    castType: 'normal', cooldown: 1, mana: 0, superArmor: 'push',
    // +0.1 s so the last tick lands before the status expires.
    stages: [stage('Buff', 0.5, [party(0.2, PARTY_R, { id: `can_mending${i + 1}`, duration: ticks * every + 0.1, value })])],
  }),
);

/** Bars each identity variant consumes. */
const BARS_FOR = new Map<string, number>([...anthems, ...mendings].map((s) => [s.id, Number(s.id.slice(-1))]));

// ------------------------------------------------------------------ tripods (facets)

const tag = (t: string) => T.custom((s) => {
  s.tags = [...(s.tags ?? []), t];
});

function actionsOf(s: ResolvedSkill): SkillAction[] {
  return s.stages.flatMap((st) => st.actions);
}

function areasOf(s: ResolvedSkill): Extract<SkillAction, { kind: 'area' }>[] {
  return actionsOf(s).filter((a): a is Extract<SkillAction, { kind: 'area' }> => a.kind === 'area');
}

/** Edits every `party` status with this id. */
function partyStatus(s: ResolvedSkill, id: string, fn: (st: StatusApply) => void): void {
  for (const a of actionsOf(s)) if (a.kind === 'party') for (const st of a.status) if (st.id === id) fn(st);
}

/** Every friendly hit (shields, zone buffs), including those carried by areas. */
function friendlyHits(s: ResolvedSkill): HitSpec[] {
  const out: HitSpec[] = [];
  forEachHit(s, (h) => {
    if (h.friendly) out.push(h);
  });
  return out;
}

/** Scales party radii (and the ally-counting ops that go with them). */
const partyRadius = (k: number) => T.custom((s) => {
  for (const a of actionsOf(s)) {
    if (a.kind === 'party') a.radius *= k;
    else if (a.kind === 'identity' && a.op === 'allies' && a.value) a.value *= k;
  }
});

/** Area +pct including the ground shapes of areas (T.aoe only scales hit shapes). */
const aoeAll = (pct: number) => T.custom((s) => {
  T.aoe(pct)(s);
  for (const a of areasOf(s)) a.area.shape = scaleShape(a.area.shape, 1 + pct);
});

function doubleChime(s: ResolvedSkill): void {
  const st = s.stages[0];
  const bolt = st?.actions.find((a) => a.kind === 'projectile');
  if (!st || !bolt) return;
  st.actions.push({ ...structuredClone(bolt), at: bolt.at + 0.18 });
  st.duration += 0.18;
}

function arcingChime(s: ResolvedSkill): void {
  const st = s.stages[0];
  const bolt = st?.actions.find((a) => a.kind === 'projectile');
  if (!st || !bolt || bolt.kind !== 'projectile' || !bolt.proj.explode) return;
  const burst = bolt.proj.explode;
  s.castType = 'point';
  s.maxRange = 14;
  st.anim = 'Cast';
  st.actions = [area(0.15, { shape: burst.shape, anchor: 'point', delay: 0.45, telegraph: 'none', hit: { ...burst, coef: burst.coef * 2.2 } })];
  st.duration = Math.max(st.duration, 0.6);
}

function wideVerse(s: ResolvedSkill): void {
  const st = s.stages[0];
  const lines = (st?.actions ?? []).filter((a): a is Extract<SkillAction, { kind: 'hit' }> => a.kind === 'hit');
  const first = lines[0];
  if (!st || !first) return;
  const coef = lines.reduce((sum, a) => sum + a.hit.coef, 0) / 3;
  // Same total damage over three 8 m lines; only the centre line strikes the weak point.
  st.actions = [-35, 0, 35].map((deg): SkillAction => ({ at: first.at, kind: 'hit', hit: { ...first.hit, shape: rect(8, 1.5, 0, deg), coef, ...(deg ? { weakPoint: 0 } : {}) } }));
}

function twin(s: ResolvedSkill): void {
  const st = s.stages[0];
  if (!st) return;
  s.castType = 'chain';
  s.stages = [st, structuredClone(st)];
  s.followWindow = 2.5;
  s.chainPenalty = 0;
}

/** Sonic Crush's slam (the area with the eruption column). */
function crushSlam(s: ResolvedSkill): { at: number; area: AreaSpec } | undefined {
  return areasOf(s).find((a) => a.area.vfx === 'pillar');
}

const quickCrush = T.custom((s) => {
  for (const a of actionsOf(s)) {
    if (a.kind === 'area') a.area.delay = Math.max(0.05, (a.area.delay ?? 0) - 0.3);
    else if (a.kind === 'identity') a.at = Math.max(0, a.at - 0.3);
  }
  const st = s.stages[0];
  if (st) st.duration = Math.max(0.5, st.duration - 0.3);
});

function frostCore(s: ResolvedSkill): void {
  const slam = crushSlam(s);
  const st = s.stages[0];
  if (!slam || !st) return;
  const r = slam.area.hit.shape.kind === 'circle' ? slam.area.hit.shape.radius : CRUSH_R;
  const t = slam.area.delay ?? 0.75;
  // Five chilling ticks drag foes in, then the core freezes them.
  st.actions.push(
    area(slam.at, { shape: circle(r), anchor: 'point', delay: t, duration: 2.5, tickInterval: 0.5, pull: 2.5, telegraph: 'none', hit: { shape: circle(r), coef: 0.3, element: 'water', stagger: 'none', weakPoint: 0, status: [{ id: 'chill', duration: 1 }] } }),
    area(slam.at, { shape: circle(r), anchor: 'point', delay: t + 2.6, telegraph: 'none', hit: { shape: circle(r), coef: 0.5, element: 'water', stagger: 'none', weakPoint: 0, status: [{ id: 'freeze', duration: 2 }] } }),
  );
}

function doubleCrush(s: ResolvedSkill): void {
  for (const st of s.stages) st.actions = st.actions.filter((a) => !(a.kind === 'area' && a.area.hit.friendly) && !(a.kind === 'identity' && a.op === 'alliesAt'));
  // The lift strikes the weak point as well.
  const lift = areasOf(s).find((a) => a.area.vfx !== 'pillar');
  if (lift) lift.area.hit.weakPoint = s.weakPoint ?? 2;
}

function crossWave(s: ResolvedSkill): void {
  const slam = crushSlam(s);
  const st = s.stages[0];
  if (!slam || !st) return;
  const t = slam.at + (slam.area.delay ?? 0.75);
  const coef = ((CRUSH_LIFT + CRUSH_SLAM) * 0.95) / 2;
  st.actions.push(
    hit(t, rect(10, 2, -5), coef, { anchor: 'point', reaction: FLINCH, weakPoint: 0 }),
    hit(t, rect(10, 2, -5, 90), coef, { anchor: 'point', reaction: FLINCH, weakPoint: 0 }),
  );
  st.actions.sort((a, b) => a.at - b.at);
  st.duration = Math.max(st.duration, t + 0.15);
}

function sanctuaryHits(s: ResolvedSkill): Extract<SkillAction, { kind: 'hit' }>[] {
  return actionsOf(s).filter((a): a is Extract<SkillAction, { kind: 'hit' }> => a.kind === 'hit' && !a.hit.friendly);
}

function extraVerse(s: ResolvedSkill): void {
  const st = s.stages[0];
  if (!st) return;
  const last = Math.max(...sanctuaryHits(s).map((a) => a.at));
  const template = st.actions.filter((a) => a.at === last).map((a): SkillAction => ({ ...structuredClone(a), at: last + 1.5 }));
  st.actions.push(...template);
  st.duration += 1.5;
}

function radiance(s: ResolvedSkill): void {
  sanctuaryHits(s)
    .sort((a, b) => a.at - b.at)
    .forEach((a, i) => {
      a.hit.coef *= 1 + 0.6 * i;
    });
}

function lightWard(s: ResolvedSkill): void {
  for (const h of friendlyHits(s)) for (const st of h.status ?? []) if (st.id === 'can_sanctuary') st.value = 0.75;
  const st = s.stages[0];
  // The self-penalty is removed by the identity as soon as the channel ends.
  if (st) st.actions.unshift(self(0, { id: 'can_lightward', duration: st.duration }));
}

/** An area that fires when Guardian Canon's protection fades. */
const onCanonExpiry = (spec: Omit<AreaSpec, 'delay'>): SkillAction => area(0.2, { telegraph: 'none', ...spec, delay: CANON_TIME });

const tripods: ClassDef['tripods'] = {
  can_chime: [
    tripod('can_chime_t1a', 1, 'Quick Prep', 'Cooldown -2 s.', T.cd(-2)),
    tripod('can_chime_t1b', 1, 'Wide Burst', 'Burst area +50%.', T.aoe(0.5)),
    tripod('can_chime_t1c', 1, 'Lingering Chime', 'Foes hit keep ringing for 2 s, taking an extra 60% in total.', T.status({ id: 'can_ring', duration: 2.05, value: (3.5 * 0.6) / 4 })),
    tripod('can_chime_t2a', 2, 'Holy Mark', 'Holy element; brands foes (+10% damage taken from your party) for 7 s.', T.element('holy'), T.synergy({ id: 'brand', duration: 7 })),
    tripod('can_chime_t2b', 2, 'Frost Chime', 'Water element; foes hit are slowed by 60% for 2 s.', T.element('water', { id: 'can_frost', duration: 2 })),
    tripod('can_chime_t2c', 2, 'Resonant Bolt', 'Damage +60%.', T.dmg(0.6)),
    tripod('can_chime_t3a', 3, 'Double Chime', 'Loose a second bolt right after the first.', T.custom(doubleChime)),
    tripod('can_chime_t3b', 3, 'Arcing Chime', 'Becomes a point skill: the chime arcs to a spot within 14 m and bursts there. Damage +120%.', T.custom(arcingChime)),
  ],
  can_refrain: [
    tripod('can_refrain_t1a', 1, 'Quick Prep', 'Cooldown -6 s.', T.cd(-6)),
    tripod('can_refrain_t1b', 1, 'Swift Cast', 'Cast speed +37%.', T.atkSpeed(0.37)),
    tripod('can_refrain_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('can_refrain_t2a', 2, 'Tempo', 'The refrain grants 8% more attack speed (+12% in total).', T.custom((s) => partyStatus(s, 'can_refrain', (st) => {
      st.value = (st.value ?? 0) + 0.08;
    }))),
    tripod('can_refrain_t2b', 2, 'Dirge', 'Foes hit deal 30% less damage for 8 s.', T.status({ id: 'can_dirge', duration: 8 })),
    tripod('can_refrain_t2c', 2, 'Wide Refrain', 'Strike area and buff radius +25%.', T.aoe(0.25), partyRadius(1.25)),
    tripod('can_refrain_t3a', 3, 'Exalt', 'Rally is 50% stronger (+18% attack power).', T.custom((s) => partyStatus(s, 'can_rally', (st) => {
      st.value = (st.value ?? 0) * 1.5;
    }))),
    tripod('can_refrain_t3b', 3, 'Solo Anthem', 'For solo play: damage +265% and you gain +20% crit rate for 20 s.', T.dmg(2.65), T.addAction(self(0.25, { id: 'can_solo', duration: 20 }), 0)),
  ],
  can_mark: [
    tripod('can_mark_t1a', 1, 'Quick Prep', 'Cooldown -7 s.', T.cd(-7)),
    tripod('can_mark_t1b', 1, 'Wide Verse', 'Three 8 m lines fan out instead of two.', T.custom(wideVerse)),
    tripod('can_mark_t1c', 1, 'Sharp Verse', 'Crit rate +40%.', T.crit(0.4)),
    tripod('can_mark_t2a', 2, 'Resonant Strike', 'Damage +70%.', T.dmg(0.7)),
    tripod('can_mark_t2b', 2, 'Barrier', 'Shield yourself for 15% of your max HP for 3 s as you cast.', T.addAction(hit(0.05, aura(0.6), 0, { friendly: true, shieldPct: 0.15, shieldDuration: 3 }), 0)),
    tripod('can_mark_t2c', 2, 'Long Mark', 'The brand lasts 14 s.', T.synergy({ id: 'brand', duration: 14 })),
    tripod('can_mark_t3a', 3, 'Aria', 'Crit damage +240%.', T.critDmg(2.4)),
    tripod('can_mark_t3b', 3, 'Echo Gain', 'Doubles the Resonance gained from its hits.', T.identityGain(1)),
  ],
  can_chorus: [
    tripod('can_chorus_t1a', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('can_chorus_t1b', 1, 'Quick Prep', 'Cooldown -4 s.', T.cd(-4)),
    tripod('can_chorus_t1c', 1, 'Swift Cast', 'Cast speed +27%.', T.atkSpeed(0.27)),
    tripod('can_chorus_t2a', 2, 'Chorus Gain', 'Doubles the Resonance gained from its hits and from allies shielded.', T.identityGain(1)),
    tripod('can_chorus_t2b', 2, 'Anthem Blast', 'Damage +145%.', T.dmg(1.45)),
    tripod('can_chorus_t2c', 2, 'Steady', 'Take 40% less damage while casting.', T.damageReduction(0.4)),
    tripod('can_chorus_t3a', 3, 'Twin Chorus', 'Can be sung twice in a row (chain). Cooldown +6 s.', T.custom(twin), T.cd(6)),
    tripod('can_chorus_t3b', 3, 'Greater Ward', 'The shield is worth 45% of your max HP.', T.custom((s) => {
      for (const h of friendlyHits(s)) if (h.shieldPct) h.shieldPct = 0.45;
    })),
  ],
  can_crush: [
    tripod('can_crush_t1a', 1, 'Quick Cast', 'The orb lands 0.3 s sooner.', quickCrush),
    tripod('can_crush_t1b', 1, 'Self Ward', 'Gain a shield worth 4% of your max HP per foe hit (up to 5 per cast).', tag('selfWard')),
    tripod('can_crush_t1c', 1, 'Tenacity', 'Gain push immunity.', T.superArmor('push')),
    tripod('can_crush_t2a', 2, 'Frost Core', 'Water element; the core drags foes in for 2.5 s, chilling them, then freezes them for 2 s.', T.element('water'), T.custom(frostCore)),
    tripod('can_crush_t2b', 2, 'Holy Wave', 'Holy element; the aftershock Rally is 10% stronger.', T.element('holy'), T.custom((s) => {
      for (const h of friendlyHits(s)) for (const st of h.status ?? []) if (st.id === 'can_rally') st.value = (st.value ?? 0) * 1.1;
    })),
    tripod('can_crush_t2c', 2, 'Double Crush', 'No Rally aftershock; damage +80% against push-immune foes and the lift strikes the weak point too.', T.custom(doubleCrush), T.vsBoss(0.8)),
    tripod('can_crush_t3a', 3, 'Great Orb', 'Area +40%.', aoeAll(0.4)),
    tripod('can_crush_t3b', 3, 'Cross Wave', 'Two 10 m shockwaves cross at the impact for an extra 95%.', T.custom(crossWave)),
  ],
  can_sanctuary: [
    tripod('can_sanct_t1a', 1, 'Free Cast', '67% chance to cost no mana.', tag('freeCast')),
    tripod('can_sanct_t1b', 1, 'Quick Prep', 'Cooldown -5 s.', T.cd(-5)),
    tripod('can_sanct_t1c', 1, 'Swift Cast', 'Cast speed +27%.', T.atkSpeed(0.27)),
    tripod('can_sanct_t2a', 2, 'Steadfast', 'Gain push immunity while channelling.', T.superArmor('push')),
    tripod('can_sanct_t2b', 2, 'Hymn Mark', 'Pulses brand foes (+10% damage taken from your party) for 6 s.', T.synergy({ id: 'brand', duration: 6 })),
    tripod('can_sanct_t2c', 2, 'Extra Verse', 'One more pulse; the hymn lasts 1.5 s longer.', T.custom(extraVerse)),
    tripod('can_sanct_t3a', 3, 'Radiance', 'Each pulse deals 60% more than the one before.', T.custom(radiance)),
    tripod('can_sanct_t3b', 3, 'Light Ward', 'Allies inside take 75% less damage, but you deal 50% less damage while channelling.', T.custom(lightWard)),
  ],
  can_prelude: [
    tripod('can_prelude_t1a', 1, 'Mana Echo', 'Recover 15 MP per foe hit (up to 5 per cast).', tag('manaEcho')),
    tripod('can_prelude_t1b', 1, 'Quick Prep', 'Cooldown -3 s.', T.cd(-3)),
    tripod('can_prelude_t1c', 1, 'Solo Storm', 'Damage +50% against normal and elite foes.', T.vsWeak(0.5)),
    tripod('can_prelude_t2a', 2, 'Wide Storm', 'Area +15%.', T.aoe(0.15)),
    tripod('can_prelude_t2b', 2, 'Chorus Gain', 'Doubles the Resonance gained from its hits.', T.identityGain(1)),
    tripod('can_prelude_t2c', 2, 'Thunder Surge', 'Damage +145%.', T.dmg(1.45)),
    tripod('can_prelude_t3a', 3, 'Static Field', 'Leaves a crackling 5 m field for 2 s that deals an extra 145%. No longer counters.', T.addAction(area(0.3, { shape: circle(5), delay: 0, duration: 2, tickInterval: 0.5, telegraph: 'none', hit: { shape: circle(5), coef: (4.5 * 1.45) / 5, element: 'lightning' } }), 0), T.counter(false)),
    tripod('can_prelude_t3b', 3, 'Focused Bolt', 'Becomes a single 11 m bolt. Damage +50%.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit') a.hit.shape = rect(11, 1.8);
    }), T.dmg(0.5)),
  ],
  can_canon: [
    tripod('can_canon_t1a', 1, 'Tenacity', 'Gain push immunity.', T.superArmor('push')),
    tripod('can_canon_t1b', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('can_canon_t1c', 1, 'Swift Cast', 'Cast speed +36%.', T.atkSpeed(0.36)),
    tripod('can_canon_t2a', 2, 'Afterglow', 'When the canon fades, the party within 24 m gains a shield worth 30% of your max HP for 4 s.', T.addAction(onCanonExpiry({ shape: circle(0.5), followCaster: true, hit: { shape: aura(PARTY_R), coef: 0, friendly: true, shieldPct: 0.3, shieldDuration: 4 } }), 0)),
    tripod('can_canon_t2b', 2, 'Iron Canon', 'Damage reduction becomes 40%.', T.custom((s) => partyStatus(s, 'can_canon', (st) => {
      st.value = 0.4;
    }))),
    tripod('can_canon_t2c', 2, 'Retribution', 'When the canon fades, it detonates where it was sung for 400%.', T.addAction(onCanonExpiry({ shape: circle(5), vfx: 'pillar', hit: { shape: circle(5), coef: 4, reaction: { kind: 'knockback', power: 2 } } }), 0)),
    tripod('can_canon_t3a', 3, 'Quick Canon', 'Cooldown -50%; all durations -50%.', T.cdPct(-0.5), T.custom((s) => {
      for (const a of actionsOf(s)) {
        if (a.kind === 'party') for (const st of a.status) st.duration *= 0.5;
        else if (a.kind === 'area') a.area.delay = (a.area.delay ?? 0) * 0.5;
      }
    })),
    tripod('can_canon_t3b', 3, 'Purity', 'The party also becomes immune to hard crowd control while the canon lasts.', T.custom((s) => {
      for (const a of actionsOf(s)) if (a.kind === 'party') a.status.push({ id: 'debuffImmune', duration: CANON_TIME });
    })),
  ],
};

// ------------------------------------------------------------------ identity

const bars = (a: Actor): number => Math.floor((a.player?.identity.gauge ?? 0) / BAR);

function addResonance(a: Actor, amount: number): void {
  const id = a.player!.identity;
  id.gauge = Math.max(0, Math.min(MAX_BARS * BAR, id.gauge + amount));
}

/** Other allied players within `r` of `p` (evaluated on the Cantor's peer from replicated positions). */
function alliesNear(world: World, a: Actor, p: V2, r: number): number {
  let n = 0;
  for (const o of world.aliveActors) if (o !== a && o.kind === 'player' && !world.isEnemy(a, o) && dist(o.pos, p) <= r) n++;
  return n;
}

/** Damaging hits one full cast lands on a single target (spreads a skill's Resonance over its hits). */
function damagingHits(acts: readonly SkillAction[]): number {
  let n = 0;
  for (const x of acts) {
    if (x.kind === 'hit') n += !x.hit.friendly && x.hit.coef > 0 ? (x.hit.count ?? 1) : 0;
    else if (x.kind === 'projectile') n += x.proj.count ?? 1;
    else if (x.kind === 'area' && !x.area.hit.friendly && x.area.hit.coef > 0) {
      const d = x.area.duration ?? 0;
      const ti = x.area.tickInterval ?? 0;
      n += 1 + (d > 0 && ti > 0 ? Math.floor(d / ti) : 0);
    }
  }
  return n;
}

function hitCount(s: SkillDef): number {
  const n = s.stages.reduce((sum, st) => sum + damagingHits(st.actions), 0);
  return Math.max(1, n);
}

/** Per-cast counter in identity data (reset on each cast of that skill). */
function bump(a: Actor, key: string, max: number): boolean {
  const d = a.player!.identity.data;
  const n = d[key] ?? 0;
  if (n >= max) return false;
  d[key] = n + 1;
  return true;
}

const identity: IdentityController = {
  gaugeMax: MAX_BARS * BAR,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = 'idle';
    id.timer = 0;
    id.data = {};
  },
  onSkillCast(world, a, s) {
    const d = a.player!.identity.data;
    d[`gain:${s.id}`] = 0;
    d[`ward:${s.id}`] = 0;
    d[`echo:${s.id}`] = 0;
    const n = BARS_FOR.get(s.id);
    if (n) addResonance(a, -n * BAR);
    if (s.tags?.includes('freeCast') && world.rng.next() < 0.67) a.mp = Math.min(a.stats.maxMp, a.mp + s.mana);
  },
  canCast(a, s) {
    const n = BARS_FOR.get(s.id);
    return !n || bars(a) >= n;
  },
  onDamageDealt(world, a, def, _amount, counter) {
    if (!def) return;
    const s: SkillDef & Partial<ResolvedSkill> = a.skills.get(def.id) ?? def;
    const d = a.player!.identity.data;
    const total = (s.identityGain ?? 0) * (s.identityGainMult ?? 1) * a.stats.identityGainMult;
    if (total > 0) {
      const key = `gain:${s.id}`;
      const got = d[key] ?? 0;
      const add = Math.min(total / hitCount(s), total - got);
      if (add > 1e-9) {
        d[key] = got + add;
        addResonance(a, add);
      }
    }
    if (counter && s.id === 'can_prelude') addResonance(a, COUNTER_GAIN * a.stats.identityGainMult);
    // Self Ward: each foe hit tops up one shield (applyShield keeps the larger value, so add onto it).
    if (s.tags?.includes('selfWard') && bump(a, `ward:${s.id}`, 5)) {
      world.applyShield(a, (a.getStatus('shield')?.shield ?? 0) + a.stats.maxHp * 0.04, 5, a.id);
    }
    if (s.tags?.includes('manaEcho') && bump(a, `echo:${s.id}`, 5)) a.mp = Math.min(a.stats.maxMp, a.mp + 15);
  },
  onKey(world, a, key) {
    const n = Math.min(MAX_BARS, bars(a));
    if (n < 1) return false;
    const s = a.skills.get(`${key === 'Z' ? 'can_anthem' : 'can_mending'}${n}`);
    return s ? world.useSkill(a, s, key) : false;
  },
  op(world, a, op, value) {
    // Resonance from buffing allies scales with facets like Chorus Gain.
    const mult = (a.cast?.skill.identityGainMult ?? 1) * a.stats.identityGainMult;
    if (op === 'gain') addResonance(a, value);
    else if (op === 'allies') addResonance(a, ALLY_GAIN * mult * alliesNear(world, a, a.pos, value));
    else if (op === 'alliesAt') addResonance(a, ALLY_GAIN * mult * alliesNear(world, a, a.cast?.aimPoint ?? a.pos, value));
  },
  update(world, a) {
    // Light Ward's self-penalty only lasts while the hymn is being channelled.
    if (a.hasStatus('can_lightward') && a.cast?.skill.id !== 'can_sanctuary') removeStatus(world, a, 'can_lightward');
  },
  describe(a) {
    const n = bars(a);
    return { label: n > 0 ? `Resonance ${n}` : 'Resonance', ready: n >= 1, active: n >= MAX_BARS, pips: n, pipsMax: MAX_BARS };
  },
};

export const CANTOR: ClassDef = {
  id: 'cantor',
  name: 'Cantor',
  title: 'Hymn-Priest',
  archetype: 'Support: buffs, brand, shields and heals',
  role: 'support',
  description: 'A bell-staff priest whose chimes and hymns steel the party. Brand the foe, Rally your allies, shield the big hits, and pour Resonance into a party-wide Anthem of Valor or a Hymn of Mending.',
  hue: HUE,
  weapon: 'bellstaff',
  model: 'hero_bard',
  baseStats: (level) => classBaseStats(level, { hp: 1.0, atk: 1.0, speed: 1.0 }),
  skills: [chimeBolt, rallyingRefrain, resonantMark, wardingChorus, sonicCrush, sanctuaryHymn, thunderPrelude, guardianCanon],
  defaultBar: ['can_chime', 'can_refrain', 'can_mark', 'can_chorus', 'can_crush', 'can_sanctuary', 'can_prelude', 'can_canon'],
  basic: {
    ...basicAttack('can_basic', 'Bell Chimes', HUE, { coefs: [0.4, 0.4, 0.7], ranged: { speed: 22, range: 9, radius: 0.45, height: 1.3, vfx: 'orb', hit: { shape: circle(0.45), coef: 0, reaction: FLINCH } } }),
    description: 'Three ringing chimes at range. Costs no mana and builds a little Resonance.',
    identityGain: 6,
  },
  dash: dashSkill('can_dash', 'Grace Step', HUE, 8, 5, { duration: 0.34 }),
  awakenings: [aegisCrescendo],
  identitySkills: [...anthems, ...mendings],
  tripods,
  identity,
  identityName: 'Resonance',
  identityDescription: 'Skill hits and buffing allies (+5% of a bar per ally) fill three Resonance bars. Z: Anthem of Valor consumes every full bar — the party within 24 m deals +5/10/15% damage for 8/12/16 s. X: Hymn of Mending consumes every full bar — the party recovers 4/6/8% max HP per tick, 8/12/16 ticks over 14/18/24 s. Keep Rally, the Resonant Brand, shields and damage reduction up.',
};
